import React, { createContext, useContext, useState, useEffect, PropsWithChildren } from 'react';

// Export ThemeMode type for TypeScript
export const THEME_MODES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system'
} as const;

export type ThemeModes = typeof THEME_MODES
export type Theme = ThemeModes[keyof ThemeModes]

interface ThemeContextType {
  mode: Theme,
  setMode: (theme: Theme) => void,
  isDark: boolean,
  themeId: string,
  changeTheme: (theme: Theme) => void,
}

// Create context
const ThemeContext = createContext<ThemeContextType|undefined>(undefined);


// Context provider
export const ThemeProvider = ({ children }: PropsWithChildren) => {
  // Get initial mode from localStorage or default to system
  const getInitialMode = ():Theme => {
    const savedMode = localStorage.getItem('themeMode');
    return savedMode as Theme || 'system';
  };

  const [mode, setModeState] = useState(getInitialMode);
  const [isDark, setIsDark] = useState(false);

  // Function to check system preference
  const getSystemPreference = () => {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  };

  // Update isDark based on mode
  useEffect(() => {
    const updateTheme = () => {
      let shouldBeDark = false;

      if (mode === 'system') {
        shouldBeDark = getSystemPreference();
      } else if (mode === 'dark') {
        shouldBeDark = true;
      }

      setIsDark(shouldBeDark);

      // Apply to document
      if (shouldBeDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    };

    updateTheme();

    // Listen for system preference changes
    if (mode === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = () => updateTheme();
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    }
  }, [mode]);

  // Function to change mode
  const setMode = (newMode: Theme) => {
    if (['light', 'dark', 'system'].includes(newMode)) {
      setModeState(newMode);
      localStorage.setItem('themeMode', newMode);
    }
  };

  return (
    <ThemeContext.Provider
      value={{
        mode,
        setMode,
        isDark,
        // Legacy support for old API
        themeId: mode,
        changeTheme: setMode
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook for using theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
